/* istanbul ignore file */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { trim } from 'lodash'
import {
  Form,
  Modal,
  Button,
  InputField,
  useApplicationNotifications,
} from '@adc/ui-adc'
import classNames from 'classnames/bind'
import { getDefaultSaveSearchNameFromFilters } from '../../converters/filters'
import styles from './styles.scss'

const cx = classNames.bind(styles)

const SaveSearchForm = ({
  savedSearch,
  closeLightbox,
  updateSavedSearch,
  currentFilters,
  listingCompatabilityFilters,
}) => {
  const notifications = useApplicationNotifications()
  const initialSavedSearchName =
    getDefaultSaveSearchNameFromFilters(currentFilters)
  const [saveSearchName, setSaveSearchName] = useState(initialSavedSearchName)
  const [showError, setShowError] = useState(false)
  const [primaryButtonText, setPrimaryButtonText] = useState('Done')

  const isSearchNameChanged = currentSearchName =>
    trim(currentSearchName) !== trim(initialSavedSearchName)

  const handleSaveSearchNameChange = e => {
    const newSearchName = e.target.value
    setSaveSearchName(newSearchName)
    setShowError(!trim(newSearchName))
    setPrimaryButtonText(isSearchNameChanged(newSearchName) ? 'Update' : 'Done')
  }

  const handleSaveButtonClick = () => {
    if (isSearchNameChanged(saveSearchName)) {
      updateSavedSearch({
        newSearchName: trim(saveSearchName),
        notifications,
        blueprintData: { currentFilters, listingCompatabilityFilters },
      })
    } else {
      closeLightbox({ notifications })
    }
  }

  return (
    <>
      <Modal.Content>
        <Form data-elm-id='save_search_form'>
          <div className={cx('header')}>
            <h2>Search Saved!</h2>
            <p>
              We will notify you through email when a new property is listed on
              Auction.com that meets your needs.
            </p>
          </div>
          <div>
            <div>
              <InputField
                label='Name your search'
                elmId='save_search_value'
                error={
                  showError ? 'Please enter a name for your search' : undefined
                }
                touched={showError}
                onChange={handleSaveSearchNameChange}
                value={saveSearchName}
              />
            </div>
          </div>
        </Form>
      </Modal.Content>

      <Modal.Actions className={cx('modal-actions-background')}>
        <Button
          elmId='remove_search_button'
          role='navigation'
          onClick={() =>
            closeLightbox({
              savedSearchToDelete: savedSearch.mostRecentSavedSearchId,
              notifications,
            })
          }
        >
          Remove Search
        </Button>
        <Button
          elmId='save_search_name_button'
          disabled={showError}
          role='primary'
          onClick={handleSaveButtonClick}
        >
          {primaryButtonText}
        </Button>
      </Modal.Actions>
    </>
  )
}

SaveSearchForm.propTypes = {
  currentFilters: PropTypes.object.isRequired,
  listingCompatabilityFilters: PropTypes.object.isRequired,
  savedSearch: PropTypes.object.isRequired,
  closeLightbox: PropTypes.func.isRequired,
  updateSavedSearch: PropTypes.func.isRequired,
}

const SaveSearchModal = ({
  savedSearch,
  closeLightbox,
  updateSavedSearch,
  currentFilters,
  listingCompatabilityFilters,
}) => {
  const notifications = useApplicationNotifications()
  return (
    <Modal
      title=''
      size='tiny'
      open={savedSearch.isModalOpen}
      onClose={() => closeLightbox({ notifications })}
    >
      {savedSearch.isModalOpen && (
        <SaveSearchForm
          closeLightbox={closeLightbox}
          savedSearch={savedSearch}
          updateSavedSearch={updateSavedSearch}
          currentFilters={currentFilters}
          listingCompatabilityFilters={listingCompatabilityFilters}
        />
      )}
    </Modal>
  )
}

SaveSearchModal.propTypes = {
  savedSearch: PropTypes.object.isRequired,
  currentFilters: PropTypes.object.isRequired,
  listingCompatabilityFilters: PropTypes.object.isRequired,
  closeLightbox: PropTypes.func.isRequired,
  updateSavedSearch: PropTypes.func.isRequired,
}

export default SaveSearchModal
